import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, List } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { diaryService } from "@/services/diaryService";
import { propertiesService } from "@/services/propertiesService";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import { formatRelativeTime } from "@/lib/format";
import { SimpleListPage } from "@/modules/_shared/SimpleListPage";
import { DiaryCalendar, type DiaryCalendarViewType } from "./DiaryCalendar";
import { PageHeader } from "@/components/molecules/PageHeader";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { usePagedResource } from "@/hooks/usePagedResource";
import type { Column } from "@/components/organisms/DataTable";
import type { DiaryEntryFilter, DiaryEntrySummary, PropertySummary } from "@/types/entities";
import { clsx } from "clsx";
import { DiaryDetailDrawer } from "./DiaryDetailDrawer";
import { DiaryCreateDrawer } from "./DiaryCreateDrawer";
import { DiaryCalendarFilters } from "./DiaryCalendarFilters";
import {
  buildDiaryFetchPlan,
  dedupeDiaryEntries,
  diaryCalendarFiltersDefault,
  diaryFilterForApi,
  fetchDiaryEntriesForPlan,
  filterDiaryEntriesByDateAndCreator,
  filterDiaryEntriesByLocalStartDateRange,
  isDiaryEntryCancelled,
  isDiaryMeetingEntry,
  type DiaryCalendarFilterState,
} from "./diaryEntryUtils";
import { formatDateAbsolute, toDiaryApiRangeEnd, toDiaryApiRangeStart } from "@/utils/date-helpers";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { scopeParams } from "@/lib/scopeParams";
import { useProfileStore } from "@/store/profileStore";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isNegotiatorUser } from "@/services/usersService";
import type { PageRequest, PageResponse, SortDirection } from "@/types/api";
import { useEffect as useReactEffect } from "react";
import { useUiStore } from "@/store/uiStore";
import { useFilterUrlSync } from "@/hooks/useFilterUrlSync";
import {
  createDiaryCalendarFilterUrlSyncConfig,
  DIARY_CALENDAR_FILTER_PARAM_KEYS,
  diaryCalendarFilterParamsPresent,
  resolveDiaryCalendarFilterFromSearchParams,
} from "./diaryCalendarFilterParams";
import {
  diaryCrmSyncFilterUrlSyncConfig,
  DIARY_CRM_SYNC_PARAM_KEYS,
  DIARY_SEARCH_PARAM_KEYS,
  diarySearchFilterUrlSyncConfig,
  parseDiaryListFilterFromSearchParams,
} from "./diaryEntryFilterParams";
import { useCreateShortcut } from "@/hooks/useCreateShortcut";

const columns: Column<DiaryEntrySummary>[] = [
  {
    id: "title",
    header: "Title",
    sortable: true,
    cell: (r) => (
      <div className="min-w-0">
        <div
          className={clsx(
            "truncate font-medium",
            isDiaryEntryCancelled(r) ? "text-bridge-muted line-through" : "text-bridge-fg",
          )}
        >
          {r.title ?? "(untitled)"}
        </div>
        <div className="truncate text-xs text-bridge-muted">{r.sourceCrm} · {r.externalId}</div>
      </div>
    ),
  },
  { id: "type", header: "Type", sortable: true, cell: (r) => r.type ?? "—" },
  {
    id: "status",
    header: "Status",
    sortable: true,
    cell: (r) =>
      r.isCancelled ? <Badge tone="danger">Cancelled</Badge>
        : r.status ? <Badge tone="accent">{r.status}</Badge>
          : <span className="text-bridge-subtle">—</span>,
  },
  { id: "startDate", header: "Start", sortable: true, cell: (r) => formatDateAbsolute(r.startDate) },
  { id: "endDate", header: "End", sortable: true, cell: (r) => formatDateAbsolute(r.endDate) },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    sortable: true,
    align: "right",
    cellTooltip: (r) =>
      [
        r.atlasCreatedBy ? `Created by ${r.atlasCreatedBy}` : null,
        r.atlasModifiedBy ? `Last modified by ${r.atlasModifiedBy}` : null,
      ]
        .filter(Boolean)
        .join("\n"),
    cell: (r) => <span className="text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
  },
];

/** Re-sort entries merged from multiple per-property requests so they match the requested page sort. */
function sortDiaryEntries(
  entries: DiaryEntrySummary[],
  sort?: string,
  direction?: SortDirection,
): DiaryEntrySummary[] {
  if (!sort) return entries;
  const dir = direction === "asc" ? 1 : -1;
  return [...entries].sort((a, b) => {
    const av = (a as unknown as Record<string, unknown>)[sort];
    const bv = (b as unknown as Record<string, unknown>)[sort];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

/**
 * Resolve a "Viewing: <property address>" label for a diary entry on the fly.
 * The list endpoint doesn't return linked properties, so we fetch the entry detail
 * (and, when the stub lacks an address, the property detail) to build the label.
 * Returns null when the entry has no linked property, so the raw title is kept.
 */
async function resolveViewingTitle(entryId: string): Promise<string | null> {
  const detail = await diaryService.getOne(entryId);
  const stub = detail.properties?.[0] as
    | (PropertySummary & { propertyRefId?: string | null; property_ref_id?: string | null })
    | undefined;
  if (!stub) return null;
  // The diary property stub carries only an externalId (label falls back to "Property
  // <externalId>"), so — like the detail drawer — fetch the full property and label from
  // the merged result to get the real address (e.g. "Hova Villas, Hove").
  const atlasId =
    (typeof stub.id === "string" && stub.id.trim() ? stub.id.trim() : "") ||
    (typeof stub.propertyRefId === "string" && stub.propertyRefId.trim() ? stub.propertyRefId.trim() : "") ||
    (typeof stub.property_ref_id === "string" && stub.property_ref_id.trim() ? stub.property_ref_id.trim() : "");
  const propertyDetail = atlasId
    ? await propertiesService.getOne(atlasId).catch(() => undefined)
    : undefined;
  const label = propertyDisplayLabel(propertyDetail ? { ...stub, ...propertyDetail } : stub);
  if (!label || label === "Unnamed Property") return null;
  return `Viewing: ${label}`;
}

function diaryDateBounds(date: string): Pick<DiaryEntryFilter, "startDate" | "endDate"> {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return {};
  const selected = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  selected.setHours(0, 0, 0, 0);
  const end = new Date(selected);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: toDiaryApiRangeStart(selected),
    endDate: toDiaryApiRangeEnd(end),
  };
}

export function DiaryPage() {
  const [searchParams] = useSearchParams();
  const [urlListFilters] = useState(() => parseDiaryListFilterFromSearchParams(searchParams));
  const { startPageLoading, stopPageLoading } = useUiStore();
  const { legalEntityId, branchId } = useSettingsStore();
  const scope = usePageScope("diary");
  // Diary spans every branch by default; the page's scope picker narrows it. `branchId` is
  // only ever set for a user pinned to a single branch, so it stays as the fallback.
  const scopeQuery = useMemo(() => scopeParams(scope), [scope]);
  // Scope diary entries to the logged-in user (from GET /profile) regardless of
  // userType. Omitted from the request when the profile has no user id.
  const userId = useProfileStore((s) => s.profileData?.userId ?? undefined);
  const profileUserType = useProfileStore((s) => s.profileData?.userType ?? null);
  const crmMappingEpoch = useCrmMappingSyncStore((s) => s.crmMappingEpoch);
  const [view, setView] = useState<"calendar" | "list">(
    searchParams.get("view") === "list" ? "list" : "calendar",
  );
  const [listReloadToken, setListReloadToken] = useState(0);
  const [listViewLoading, setListViewLoading] = useState(false);
  const [selected, setSelected] = useState<DiaryEntrySummary | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOccurrenceDate, setSelectedOccurrenceDate] = useState<Date | null>(null);
  const [createDate, setCreateDate] = useState<Date | null>(null);
  const [calendarView, setCalendarView] = useState<DiaryCalendarViewType>("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const { currentUser, isAdmin } = useCurrentUser(legalEntityId);
  const openCreateDiaryEntry = useCallback(() => {
    setDrawerOpen(false);
    setSelected(null);
    setSelectedOccurrenceDate(null);
    setCreateDate((current) => current ?? new Date());
  }, []);
  useCreateShortcut(openCreateDiaryEntry);
  const isNegotiator =
    isNegotiatorUser(currentUser) || profileUserType?.trim().toUpperCase() === "NEGOTIATOR";
  const hadCalendarFiltersInUrl = useRef(diaryCalendarFilterParamsPresent(searchParams));
  // Negotiators default to their own events; everyone else gets the shared default.
  const [filters, setFilters] = useState<DiaryCalendarFilterState>(() =>
    resolveDiaryCalendarFilterFromSearchParams(searchParams, isNegotiator),
  );
  const calendarFilterUrlSyncConfig = useMemo(
    () => createDiaryCalendarFilterUrlSyncConfig(isNegotiator),
    [isNegotiator],
  );
  const setCalendarFiltersFromUrl = useCallback((next: Partial<DiaryCalendarFilterState>) => {
    setFilters((prev) => ({
      ...prev,
      ...next,
      userIds: next.userIds ?? prev.userIds,
      propertyIds: next.propertyIds ?? prev.propertyIds,
      availabilityPropertyIds: next.availabilityPropertyIds ?? prev.availabilityPropertyIds,
    }));
  }, []);
  // `diaryFilterForApi` rewrites userId from the fetch plan (All Events / Me only),
  // so the profile id is passed through unconditionally here.
  const meUserId = userId ?? currentUser?.id;

  const isInitialMount = useRef(true);
  const prevScopeRef = useRef({ legalEntityId, branchId });

  useEffect(() => {
    const scopeChanged =
      prevScopeRef.current.legalEntityId !== legalEntityId ||
      prevScopeRef.current.branchId !== branchId;
    prevScopeRef.current = { legalEntityId, branchId };

    if (scopeChanged && !isInitialMount.current) {
      hadCalendarFiltersInUrl.current = false;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (hadCalendarFiltersInUrl.current) return;
    } else if (hadCalendarFiltersInUrl.current && !scopeChanged) {
      return;
    }

    setFilters(diaryCalendarFiltersDefault(isNegotiator));
  }, [legalEntityId, branchId, isNegotiator]);

  useFilterUrlSync(filters, setCalendarFiltersFromUrl, calendarFilterUrlSyncConfig);

  useEffect(() => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(filters.date);
    if (!match) return;
    const next = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    setCalendarDate((current) =>
      current.getFullYear() === next.getFullYear()
      && current.getMonth() === next.getMonth()
      && current.getDate() === next.getDate()
        ? current
        : next,
    );
  }, [filters.date]);

  /** Full Sunday–Saturday weeks covering the month (used for API fetch so all views share one dataset). */
  const monthGridBounds = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    const rangeStart = new Date(monthStart);
    rangeStart.setDate(monthStart.getDate() - monthStart.getDay());
    const rangeEnd = new Date(monthEnd);
    rangeEnd.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
    rangeStart.setHours(0, 0, 0, 0);
    rangeEnd.setHours(23, 59, 59, 999);
    return {
      rangeStart,
      rangeEnd,
      startDate: toDiaryApiRangeStart(rangeStart),
      endDate: toDiaryApiRangeEnd(rangeEnd),
    };
  }, [calendarDate.getFullYear(), calendarDate.getMonth()]);

  /** Visible window for the active calendar view (client-side filter). */
  const displayBounds = useMemo(() => {
    const date = calendarDate.getDate();

    let rangeStart: Date;
    let rangeEnd: Date;

    if (calendarView === "month") {
      rangeStart = monthGridBounds.rangeStart;
      rangeEnd = monthGridBounds.rangeEnd;
    } else if (calendarView === "week") {
      rangeStart = new Date(calendarDate);
      rangeStart.setDate(date - calendarDate.getDay());
      rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeStart.getDate() + 6);
    } else {
      rangeStart = new Date(calendarDate);
      rangeEnd = new Date(calendarDate);
    }

    rangeStart.setHours(0, 0, 0, 0);
    rangeEnd.setHours(23, 59, 59, 999);

    return { rangeStart, rangeEnd };
  }, [calendarDate, calendarView, monthGridBounds.rangeStart, monthGridBounds.rangeEnd]);

  const listScanAbortRef = useRef<AbortController | null>(null);
  const tableScanAbortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      listScanAbortRef.current?.abort();
      tableScanAbortRef.current?.abort();
    },
    [],
  );

  const diaryListFetcher = useCallback(
    async (
      filter: DiaryEntryFilter,
      page: PageRequest,
    ): Promise<PageResponse<DiaryEntrySummary>> => {
      const plan = buildDiaryFetchPlan(filters, meUserId);
      const sortParams = { sort: page.sort, direction: page.direction };
      const effectiveFilter = { ...filter, ...diaryDateBounds(filters.date) };
      listScanAbortRef.current?.abort();
      const controller = new AbortController();
      listScanAbortRef.current = controller;
      const fetched = await fetchDiaryEntriesForPlan(
        effectiveFilter,
        plan,
        sortParams,
        filters.createdBy
          ? (f, sort) => diaryService.listAll(f, sort, Infinity, controller.signal)
          : (f, sort) => diaryService.listAll(f, sort, undefined, controller.signal),
      );
      const content = filterDiaryEntriesByDateAndCreator(fetched, filters.date, filters.createdBy);
      return {
        content,
        page: 0,
        size: content.length,
        totalElements: content.length,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      };
    },
    [crmMappingEpoch, filters, meUserId],
  );

  const tableScanCacheRef = useRef<{ key: string; promise: Promise<DiaryEntrySummary[]> } | null>(null);

  // Leaving list view drops the table scan so returning refetches current entries.
  useEffect(() => {
    if (view === "list") return;
    tableScanAbortRef.current?.abort();
    tableScanCacheRef.current = null;
  }, [view]);

  const diaryTableFetcher = useCallback(
    async (
      filter: DiaryEntryFilter,
      page: PageRequest,
    ): Promise<PageResponse<DiaryEntrySummary>> => {
      const plan = buildDiaryFetchPlan(filters, meUserId);
      const sortParams = { sort: page.sort, direction: page.direction };
      const effectiveFilter = { ...filter, ...diaryDateBounds(filters.date) };

      const canUseServerPagination =
        !filters.createdBy &&
        !plan?.propertySpecs?.length &&
        ((plan.includeAllEvents && plan.allowedUserIds.size === 0) ||
          (plan.meOnly && Boolean(plan.meUserId)));

      if (canUseServerPagination) {
        tableScanAbortRef.current?.abort();
        tableScanCacheRef.current = null;
        return diaryService.list(diaryFilterForApi(effectiveFilter, plan), page);
      }

      // Reuse the completed scan across page requests; any change to the effective filter,
      // filters (fetch plan), user, sort, CRM mapping epoch or reload token drops it.
      const cacheKey = JSON.stringify([
        effectiveFilter,
        filters,
        meUserId,
        sortParams,
        crmMappingEpoch,
        listReloadToken,
      ]);
      let scan = tableScanCacheRef.current;
      if (!scan || scan.key !== cacheKey) {
        tableScanAbortRef.current?.abort();
        const controller = new AbortController();
        tableScanAbortRef.current = controller;
        const promise = fetchDiaryEntriesForPlan(
          effectiveFilter,
          plan,
          sortParams,
          // Creator is filtered client-side (no API param), so the scan must reach every
          // page; otherwise matches beyond the default page cap would be silently lost.
          filters.createdBy
            ? (f, sort) => diaryService.listAll(f, sort, Infinity, controller.signal)
            : (f, sort) => diaryService.listAll(f, sort, undefined, controller.signal),
        );
        scan = { key: cacheKey, promise };
        tableScanCacheRef.current = scan;
        promise.catch(() => {
          if (tableScanCacheRef.current === scan) tableScanCacheRef.current = null;
        });
      }
      const merged = sortDiaryEntries(
        filterDiaryEntriesByDateAndCreator(await scan.promise, filters.date, filters.createdBy),
        page.sort,
        page.direction,
      );
      const pageSize = page.size ?? 50;
      const pageIndex = page.page ?? 0;
      const start = pageIndex * pageSize;
      return {
        content: merged.slice(start, start + pageSize),
        page: pageIndex,
        size: pageSize,
        totalElements: merged.length,
        totalPages: Math.max(1, Math.ceil(merged.length / pageSize)),
        hasNext: start + pageSize < merged.length,
        hasPrevious: pageIndex > 0,
      };
    },
    [crmMappingEpoch, filters, meUserId, listReloadToken],
  );

  const list = usePagedResource<DiaryEntrySummary, DiaryEntryFilter>(diaryListFetcher, {
    initialFilter: {
      legalEntityId: legalEntityId ?? undefined,
      branchId: branchId ?? undefined,
      userId,
      ...scopeQuery,
      ...urlListFilters,
    },
    initialPage: { page: 0, size: 50, sort: "startDate", direction: "desc" },
  });

  useFilterUrlSync(list.filter, list.setFilter, diaryCrmSyncFilterUrlSyncConfig);

  useReactEffect(() => {
    const baseFilter = {
      legalEntityId: legalEntityId ?? undefined,
      branchId: branchId ?? undefined,
      userId,
      // Explicitly cleared, not merely omitted: setFilter replaces the filter wholesale, so
      // an emptied selection has to overwrite the previous scope rather than leave it behind.
      legalEntityIds: scopeQuery.legalEntityIds,
      branchIds: scopeQuery.branchIds,
    };
    if (view === "calendar") {
      list.setFilter({
        ...baseFilter,
        startDate: monthGridBounds.startDate,
        endDate: monthGridBounds.endDate,
      });
    } else {
      list.setFilter({
        ...baseFilter,
        startDate: undefined,
        endDate: undefined,
      });
    }
  }, [legalEntityId, branchId, userId, view, monthGridBounds, scopeQuery]);

  const visibleEntries = useMemo(
    () =>
      filterDiaryEntriesByLocalStartDateRange(
        dedupeDiaryEntries(list.data?.content ?? []),
        displayBounds.rangeStart,
        displayBounds.rangeEnd,
      ),
    [list.data?.content, displayBounds.rangeStart, displayBounds.rangeEnd],
  );

  // `selected` is the row as it was when clicked, so it never sees a reload.
  // Re-read the live row by id — only `GET /diary-entries` carries `crmSyncStatus`.
  const selectedCrmSyncStatus = useMemo(() => {
    if (!selected) return undefined;
    const live = list.data?.content.find((r) => r.id === selected.id);
    return live?.crmSyncStatus ?? live?.syncAlto ?? selected.crmSyncStatus ?? selected.syncAlto;
  }, [list.data?.content, selected]);

  // Resolve "Viewing: <property>" labels on the fly for the entries currently on the
  // calendar. Cached per entry id so navigation/re-renders don't refetch. Entries with no
  // linked property resolve to null and keep their own title (mutates nothing server-side).
  const [entryTitleOverrides, setEntryTitleOverrides] = useState<Record<string, string>>({});
  // Ids whose title has been resolved (regardless of whether a property was found), used
  // both to dedupe fetches and to know when the first resolution pass is complete.
  const [resolvedTitleIds, setResolvedTitleIds] = useState<Set<string>>(new Set());
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const titleInFlightRef = useRef<Set<string>>(new Set());

  useReactEffect(() => {
    const inFlight = titleInFlightRef.current;
    // Only meeting-category entries get the "Viewing: <property>" relabel — never viewings
    // (or other types), so we also skip the extra API calls for them.
    const toFetch = visibleEntries.filter(
      (e) => e.id && isDiaryMeetingEntry(e) && !resolvedTitleIds.has(e.id) && !inFlight.has(e.id),
    );
    if (toFetch.length === 0) return;
    let cancelled = false;
    toFetch.forEach((e) => inFlight.add(e.id));
    void Promise.all(
      toFetch.map(async (e) => {
        const title = await resolveViewingTitle(e.id).catch(() => null);
        inFlight.delete(e.id);
        return [e.id, title] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      const additions: Record<string, string> = {};
      for (const [id, title] of pairs) if (title) additions[id] = title;
      if (Object.keys(additions).length) {
        setEntryTitleOverrides((prev) => ({ ...prev, ...additions }));
      }
      setResolvedTitleIds((prev) => {
        const next = new Set(prev);
        for (const [id] of pairs) next.add(id);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [visibleEntries, resolvedTitleIds]);

  const meetingTitlesPending = useMemo(
    () =>
      visibleEntries.some(
        (e) =>
          e.id &&
          isDiaryMeetingEntry(e) &&
          !resolvedTitleIds.has(e.id) &&
          !entryTitleOverrides[e.id],
      ),
    [visibleEntries, resolvedTitleIds, entryTitleOverrides],
  );

  // First visit only: wait for diary data + meeting title relabels. Refresh keeps cached
  // overrides so chips stay "Viewing: …" and we don't re-fetch every property detail.
  const dataLoading = view === "list" ? listViewLoading : list.loading;
  const initialLoading = !list.data || meetingTitlesPending;
  useReactEffect(() => {
    if (!initialLoadDone && !initialLoading) setInitialLoadDone(true);
  }, [initialLoadDone, initialLoading]);

  const showPageLoader = !initialLoadDone ? initialLoading : dataLoading;

  useReactEffect(() => {
    if (showPageLoader) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [showPageLoader, startPageLoading, stopPageLoading]);

  const handleSyncStatusChange = useCallback(
    (value: string) => {
      list.setFilter({ crmSyncStatus: value || undefined });
      titleInFlightRef.current.clear();
    },
    [list],
  );

  const onSelect = (row: DiaryEntrySummary, occurrenceDate?: Date) => {
    setCreateDate(null);
    setSelected(row);
    setSelectedOccurrenceDate(occurrenceDate ?? null);
    setDrawerOpen(true);
  };

  const onDateClick = (date: Date) => {
    setDrawerOpen(false);
    setSelected(null);
    setCreateDate(date);
  };

  const handleRefresh = useCallback(() => {
    if (view === "list") {
      setListReloadToken((token) => token + 1);
      return;
    }

    titleInFlightRef.current.clear();
    list.reload();
  }, [view, list]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between pr-6">
        <PageHeader
          title="Diary"
          subtitle="Appointments, viewings, inspections, follow-ups."
        />
        <div className="flex items-center gap-2">
          <SavedFiltersButton
            pageName="diary"
            filterParamKeys={[
              ...DIARY_CALENDAR_FILTER_PARAM_KEYS,
              ...DIARY_CRM_SYNC_PARAM_KEYS,
              ...DIARY_SEARCH_PARAM_KEYS,
            ]}
            scopePage="diary"
          />
          <div className="w-[180px]">
            <MenuSelect
              value={list.filter.crmSyncStatus ?? ""}
              onChange={handleSyncStatusChange}
              options={[
                { value: "", label: "Any sync status" },
                { value: "COMPLETED", label: "In Sync" },
                { value: "IN_PROGRESS", label: "In Progress" },
                { value: "FAILED", label: "Failed" },
                { value: "CHECKPOINT", label: "Checkpoint" }
              ]}
            />
          </div>
          <ScopePicker page="diary" />
          <DiaryCalendarFilters
            legalEntityId={legalEntityId ?? undefined}
            branchId={branchId ?? undefined}
            currentUser={currentUser}
            isAdmin={isAdmin}
            showAllEventsFilter={!isNegotiator}
            value={filters}
            onChange={setFilters}
          />
          <RefreshButton onClick={handleRefresh} loading={dataLoading} />
          <div className="flex items-center bg-bridge-surface/60 rounded-lg border border-bridge-border/60 p-0.5">
            <button
              onClick={() => setView("calendar")}
              className={clsx(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                view === "calendar" ? "bg-bridge-accent text-white shadow-sm" : "text-bridge-muted hover:text-bridge-fg"
              )}
            >
              <LayoutGrid size={14} /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              className={clsx(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                view === "list" ? "bg-bridge-accent text-white shadow-sm" : "text-bridge-muted hover:text-bridge-fg"
              )}
            >
              <List size={14} /> List
            </button>
          </div>
        </div>
      </div>

      {view === "list" ? (
        <div className="flex-1 overflow-hidden">
          <SimpleListPage<DiaryEntrySummary, DiaryEntryFilter>
            title=""
            fetcher={diaryTableFetcher}
            columns={columns}
            rowKey={(r) => r.id}
            onRowClick={onSelect}
            markVisitedRow
            searchPlaceholder="Search by title or notes..."
            emptyTitle="No diary entries match"
            initialFilter={urlListFilters}
            filterUrlSync={diarySearchFilterUrlSyncConfig}
            reloadToken={listReloadToken}
            filterSync={{ crmSyncStatus: list.filter.crmSyncStatus }}
            onLoadingChange={setListViewLoading}
          />
        </div>
      ) : (
        <div
          className={clsx(
            "flex-1 mx-6 mb-6 glass rounded-lg overflow-hidden border border-bridge-border/60",
            !initialLoadDone && showPageLoader && "invisible",
          )}
        >
          <DiaryCalendar
            entries={visibleEntries}
            view={calendarView}
            onViewChange={setCalendarView}
            currentDate={calendarDate}
            onCurrentDateChange={setCalendarDate}
            onEntryClick={onSelect}
            onDateClick={onDateClick}
            filterDate={filters.date}
            entryTitleOverrides={entryTitleOverrides}
          />
        </div>
      )}

      <DiaryCreateDrawer
        open={createDate != null}
        date={createDate}
        legalEntityId={legalEntityId ?? undefined}
        branchId={branchId ?? undefined}
        onClose={() => setCreateDate(null)}
        onCreated={() => {
          list.reload();
          setListReloadToken(t => t + 1);
        }}
      />

      <DiaryDetailDrawer
        open={drawerOpen}
        entryId={selected?.id ?? null}
        crmSyncStatus={selectedCrmSyncStatus}
        occurrenceDate={selectedOccurrenceDate}
        onClose={() => { setDrawerOpen(false); setSelectedOccurrenceDate(null); }}
        onSaved={(entry) => {
          if (entry) list.upsertEntry(entry);
          list.reload();
          setListReloadToken(t => t + 1);
        }}
        onResynced={() => {
          list.reload();
          setListReloadToken(t => t + 1);
        }}
      />
    </div>
  );
}
